import { useParams, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Stack } from "@altimateai/lego";
import { Alert, AlertDescription } from "@altimateai/lego";
import { getWarehouse, getWarehouseAutoTuneConfiguration } from "@/helpers/apis";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import {
  GET_WAREHOUSE_BY_ID,
  GET_AUTO_TUNE_CONFIGURATION,
  defaultAutoTuneConfig,
  WH_DateRange_Prefix,
} from "./constants";
import WarehouseDetailsHeader from "./components/details/WarehouseDetailsHeader";
import WarehouseKeyInfo from "./components/details/WarehouseKeyInfo";
import WarehouseSavings from "./components/details/WarehouseSavings";
import WarehouseInsights from "./components/details/WarehouseInsights";
import WarehouseDetailsTabs from "./components/details/WarehouseDetailsTabs";
import { RelativeComponentLoader } from "@/components/loader/Loader";
import { useBreadcrumb, getEntityName } from "@/components/breadcrumbs";

const NewWarehouseDetails = () => {
  const { instance_id, warehouse_rk } = useParams<{
    instance_id: string;
    warehouse_rk: string;
  }>();
  const { state } = useLocation();

  const { startDate, endDate } = useDateFromSearchParams(
    dayjs().subtract(1, "month").toDate(),
    dayjs().toDate(),
    WH_DateRange_Prefix
  );

  const {
    data: warehouseDetails,
    isLoading,
    error,
  } = useQuery({
    queryKey: [GET_WAREHOUSE_BY_ID, instance_id, warehouse_rk],
    queryFn: () => getWarehouse(instance_id!, warehouse_rk!, { period: 30 }),
    enabled: !!instance_id && !!warehouse_rk,
  });

  const { data: autoTuneConfiguration = defaultAutoTuneConfig } = useQuery({
    queryKey: [GET_AUTO_TUNE_CONFIGURATION, instance_id, warehouse_rk],
    queryFn: () => getWarehouseAutoTuneConfiguration(instance_id!, warehouse_rk!),
    enabled: !!instance_id && !!warehouse_rk,
  });

  // Use location state as fallback for instant display
  useBreadcrumb(getEntityName(state) ?? warehouseDetails?.warehouse_name ?? "Loading...", {
    defaultParents: [{ label: "Warehouses", pathname: "/warehouse" }],
  });

  if (isLoading) {
    return <RelativeComponentLoader label="Loading warehouse details..." />;
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {error instanceof Error
            ? error.message
            : String(error) || "Failed to load warehouse details"}
        </AlertDescription>
      </Alert>
    );
  }

  if (!warehouseDetails) {
    return null;
  }

  return (
    <Stack direction="column" gap="lg">
      <WarehouseDetailsHeader
        warehouseDetails={warehouseDetails}
        autoTuneConfiguration={autoTuneConfiguration}
      />

      <div>
        <WarehouseKeyInfo warehouseDetails={warehouseDetails} />
        <WarehouseSavings
          warehouseRK={warehouseDetails.warehouse_rk}
          startDate={startDate}
          endDate={endDate}
        />
      </div>
      <WarehouseInsights warehouseDetails={warehouseDetails} />
      <WarehouseDetailsTabs
        warehouseDetails={warehouseDetails}
        startDate={startDate}
        endDate={endDate}
        isSchedulingEnabled={true}
        isDynamicSchedule={
          autoTuneConfiguration?.resizing_configuration?.resizing_configuration?.mode ===
          "auto_resize"
        }
      />
    </Stack>
  );
};

export default NewWarehouseDetails;
